import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CATEGORIES, LEGEND_ORDER, catColor, categorizeEvent } from '../../config/categories';
import { normalizeEventTags } from '../../utils/eventUtils';
import { fs } from '../../utils/typography';

const TOP_TAGS = 8;
const HINT_RESERVED_WIDTH = 50; // approx width of the "+N" hint pill, reserved so it never itself gets clipped
const COMPACT_BREAKPOINT = 1000; // below this row width, category pills drop their label (icon + count only)

// Shared filter-pill row: category pills (icon + label + count) followed by
// tag pills (label + count only, no icon). Used inline in the desktop
// CalendarLegend footer and inside the mobile OrganizationFilter panel, so
// both surfaces derive counts the same way and stay in sync automatically.
//
// `events` should be the event set *before* category/tag filters are applied
// (but after org/starred/private/date-range filters) so category counts
// reflect "if I picked this category, how many events would show". Tag
// counts are scoped to the selected category (or the full set if none).
//
// Two independent ways to handle a tag list too long to show all at once —
// pick one, not both:
//   - `capTags` (default true): cap at the top TOP_TAGS by count, with a
//     "…"/"Show less" toggle to reveal the rest. Used by the mobile filter
//     panel, which has vertical room to spare but shouldn't dump dozens of
//     tags on screen unasked.
//   - `fitTagsToWidth`: categories always render in full, at a fixed size;
//     tags are measured against the available width and only as many whole
//     pills as actually fit are shown, plus a "+N" hint for the rest. Used
//     by the desktop legend, which needs to stay a fixed one-line height.
const CategoryTagFilters = ({
    tokens, events, selectedCategory, onCategoryClick = () => {}, selectedTags, onTagClick = () => {},
    capTags = true, fitTagsToWidth = false,
}) => {
    const c = tokens.colors;
    const [showAllTags, setShowAllTags] = useState(false);
    const rowRef = useRef(null);
    const tagPillRefs = useRef([]);
    const [fitCount, setFitCount] = useState(null); // fitTagsToWidth mode only; null = not yet measured
    const [isCompact, setIsCompact] = useState(false); // fitTagsToWidth mode only; row too narrow for category labels
    const [isHiddenTagsOpen, setIsHiddenTagsOpen] = useState(false); // fitTagsToWidth mode only
    const [hiddenTagsPopoverPos, setHiddenTagsPopoverPos] = useState(null); // {top, left}, viewport coords
    const hiddenTagsButtonRef = useRef(null);
    const hiddenTagsPopoverRef = useRef(null);

    const categoryCounts = useMemo(() => {
        const counts = {};
        (events || []).forEach((event) => {
            const cat = categorizeEvent(event);
            counts[cat] = (counts[cat] || 0) + 1;
        });
        return counts;
    }, [events]);

    const categoryScopedEvents = useMemo(() => {
        if (!selectedCategory) return events || [];
        return (events || []).filter((event) => categorizeEvent(event) === selectedCategory);
    }, [events, selectedCategory]);

    const tagList = useMemo(() => {
        const byKey = new Map(); // lowercase tag -> { label, count }
        categoryScopedEvents.forEach((event) => {
            normalizeEventTags(event.tags).forEach((tag) => {
                const key = tag.toLowerCase();
                const existing = byKey.get(key);
                if (existing) {
                    existing.count += 1;
                } else {
                    byKey.set(key, { key, label: tag, count: 1 });
                }
            });
        });
        return [...byKey.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
    }, [categoryScopedEvents]);

    const tagPoolKey = tagList.map((t) => t.key).join('|');

    // capTags mode: reset the "show all" expansion whenever the tag pool
    // changes underneath it (e.g. switching category) so it doesn't stay
    // stuck open on a short list.
    useEffect(() => {
        setShowAllTags(false);
    }, [tagPoolKey]);

    // fitTagsToWidth mode: close the hidden-tags popover whenever the tag
    // pool changes underneath it, and on outside click / Escape / resize /
    // scroll (its position is computed once on open, not tracked live).
    useEffect(() => {
        setIsHiddenTagsOpen(false);
    }, [tagPoolKey]);

    useEffect(() => {
        if (!isHiddenTagsOpen) return undefined;
        const handleClickOutside = (e) => {
            if (hiddenTagsButtonRef.current && hiddenTagsButtonRef.current.contains(e.target)) return;
            if (hiddenTagsPopoverRef.current && !hiddenTagsPopoverRef.current.contains(e.target)) {
                setIsHiddenTagsOpen(false);
            }
        };
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') setIsHiddenTagsOpen(false);
        };
        const handleReflow = () => setIsHiddenTagsOpen(false);
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);
        window.addEventListener('resize', handleReflow);
        window.addEventListener('scroll', handleReflow, true);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('resize', handleReflow);
            window.removeEventListener('scroll', handleReflow, true);
        };
    }, [isHiddenTagsOpen]);

    // fitTagsToWidth mode: force a full re-render (all tags) whenever the tag
    // pool changes, so the measurement pass below has true natural widths to
    // work from rather than whatever count last happened to fit.
    useEffect(() => {
        if (fitTagsToWidth) setFitCount(null);
    }, [fitTagsToWidth, tagPoolKey]);

    // fitTagsToWidth mode: re-measure whenever the row's own width changes
    // (window resize, sidebar opening, text-size toggle, etc).
    useEffect(() => {
        if (!fitTagsToWidth) return undefined;
        const row = rowRef.current;
        if (!row) return undefined;
        const resizeObserver = new ResizeObserver(() => setFitCount(null));
        resizeObserver.observe(row);
        return () => resizeObserver.disconnect();
    }, [fitTagsToWidth]);

    // fitTagsToWidth mode: the actual measurement. Runs synchronously before
    // paint (useLayoutEffect), so even though it renders all tags first to
    // measure their true widths, the browser never paints that intermediate
    // state — only the final, correctly-fitted one.
    useLayoutEffect(() => {
        if (!fitTagsToWidth || fitCount !== null) return;
        const row = rowRef.current;
        if (!row) return;

        const containerWidth = row.clientWidth;

        // Settle isCompact first — it changes how wide the category pills
        // themselves are, so tag-fit math against a stale layout would be
        // wrong. Flipping it re-renders with the new pill sizes and this
        // effect runs again (isCompact is a dependency below) before paint.
        const nextCompact = containerWidth < COMPACT_BREAKPOINT;
        if (nextCompact !== isCompact) {
            setIsCompact(nextCompact);
            return;
        }

        const rowLeft = row.getBoundingClientRect().left;
        let fit = tagPillRefs.current.length;
        for (let i = 0; i < tagPillRefs.current.length; i++) {
            const el = tagPillRefs.current[i];
            if (!el) continue;
            if (el.getBoundingClientRect().right - rowLeft > containerWidth) {
                fit = i;
                break;
            }
        }
        if (fit < tagList.length) {
            // Leave room for the "+N" hint pill so it doesn't get clipped itself.
            while (fit > 0) {
                const el = tagPillRefs.current[fit - 1];
                if (!el) break;
                if (el.getBoundingClientRect().right - rowLeft + HINT_RESERVED_WIDTH <= containerWidth) break;
                fit -= 1;
            }
        }
        setFitCount(fit);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fitTagsToWidth, fitCount, tagList, isCompact]);

    let visibleTags;
    let hiddenTagCount;
    if (fitTagsToWidth) {
        visibleTags = fitCount === null ? tagList : tagList.slice(0, fitCount);
        hiddenTagCount = fitCount === null ? 0 : tagList.length - fitCount;
    } else {
        visibleTags = !capTags || showAllTags ? tagList : tagList.slice(0, TOP_TAGS);
        hiddenTagCount = capTags ? tagList.length - visibleTags.length : 0;
    }

    // Collect refs for this render's tag pills so the layout effect above can
    // measure them. Safe to mutate during render — it's bookkeeping only and
    // doesn't affect this render's own output.
    tagPillRefs.current = [];

    const pillBase = {
        display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer', flexShrink: 0,
        fontFamily: tokens.fonts.ui, fontSize: fs(13), fontWeight: 500,
        padding: '4px 10px', borderRadius: 999, border: `1px solid ${c.gridLine}`,
        background: 'transparent', color: c.muted, transition: 'opacity 0.15s, background 0.15s, color 0.15s',
    };

    const countStyle = (active) => ({
        fontFamily: tokens.fonts.mono, fontSize: fs(10.5), color: active ? 'inherit' : c.faint, opacity: active ? 0.85 : 1,
    });

    const POPOVER_WIDTH = 320;
    const POPOVER_MAX_HEIGHT = 260;
    const toggleHiddenTagsPopover = () => {
        if (isHiddenTagsOpen) {
            setIsHiddenTagsOpen(false);
            return;
        }
        const btn = hiddenTagsButtonRef.current;
        if (btn) {
            const rect = btn.getBoundingClientRect();
            const margin = 8;
            const gap = 4; // hug the button rather than floating away from it
            // Opens down-and-to-the-right by default — left edge anchored to the
            // button's own left edge — only sliding left if that would run off
            // the viewport's right edge.
            const left = Math.max(margin, Math.min(rect.left, window.innerWidth - POPOVER_WIDTH - margin));
            // The legend sits at the bottom of the page, so there's usually more
            // room above the button than below it — flip upward when that's the
            // case, and shrink to whatever room is actually available either way.
            const spaceBelow = window.innerHeight - rect.bottom - margin;
            const spaceAbove = rect.top - margin;
            const openUpward = spaceBelow < 160 && spaceAbove > spaceBelow;
            const maxHeight = Math.max(120, Math.min(POPOVER_MAX_HEIGHT, openUpward ? spaceAbove : spaceBelow));
            // Anchor by the edge nearest the button (bottom when opening upward,
            // top when opening downward) rather than computing the far edge from
            // an assumed height — the popover is usually shorter than maxHeight
            // (its height tracks content), so anchoring the far edge would leave
            // a gap between it and the button instead of hugging it.
            setHiddenTagsPopoverPos(openUpward
                ? { bottom: window.innerHeight - rect.top + gap, left, maxHeight }
                : { top: rect.bottom + gap, left, maxHeight });
        }
        setIsHiddenTagsOpen(true);
    };

    // Shared tag-pill renderer — used for the row itself and, in
    // fitTagsToWidth mode, again inside the hidden-tags popover so both
    // stay visually and behaviorally identical.
    const renderTagPill = (tag, pillRef) => {
        const isActive = selectedTags && selectedTags.has(tag.key);
        return (
            <div
                key={tag.key}
                ref={pillRef}
                role="button"
                tabIndex={0}
                onClick={() => onTagClick(tag.key, tag.label)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTagClick(tag.key, tag.label); } }}
                aria-pressed={isActive}
                aria-label={`${isActive ? 'Remove' : 'Add'} tag filter ${tag.label} (${tag.count} event${tag.count === 1 ? '' : 's'})`}
                style={{
                    ...pillBase,
                    background: isActive ? tokens.colors.accent : 'transparent',
                    border: `1px solid ${isActive ? tokens.colors.accent : c.gridLine}`,
                    color: isActive ? tokens.mode === 'dark' ? '#1a1410' : '#fff' : c.muted,
                }}
            >
                <span>{tag.label}</span>
                <span style={countStyle(isActive)}>{tag.count}</span>
            </div>
        );
    };

    const content = (
        <>
            <span style={{
                fontFamily: tokens.fonts.mono, fontSize: fs(11), letterSpacing: 0.6,
                textTransform: 'uppercase', color: c.faint, fontWeight: 600, flexShrink: 0,
            }}>Categories</span>
            {LEGEND_ORDER.map((cat) => {
                const cc = catColor(cat, tokens);
                const count = categoryCounts[cat] || 0;
                const isActive = selectedCategory === cat;
                const isDimmed = selectedCategory && !isActive;
                return (
                    <div
                        key={cat}
                        role="button"
                        tabIndex={0}
                        onClick={() => onCategoryClick(cat)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onCategoryClick(cat); } }}
                        aria-pressed={isActive}
                        aria-label={`${isActive ? 'Clear' : 'Filter by'} ${CATEGORIES[cat].label} (${count} event${count === 1 ? '' : 's'})`}
                        title={fitTagsToWidth && isCompact ? CATEGORIES[cat].label : undefined}
                        style={{
                            ...pillBase,
                            opacity: isDimmed ? 0.4 : 1,
                            background: isActive ? cc.fillBg : 'transparent',
                            border: `1px solid ${isActive ? cc.strong : c.gridLine}`,
                            color: isActive ? cc.fillText : c.muted,
                        }}
                    >
                        <i className={`fas ${CATEGORIES[cat].icon}`} style={{ color: cc.strong, fontSize: fs(12), width: 14, textAlign: 'center' }} aria-hidden="true" />
                        {!(fitTagsToWidth && isCompact) && <span>{CATEGORIES[cat].label}</span>}
                        <span style={countStyle(isActive)}>{count}</span>
                    </div>
                );
            })}

            {tagList.length > 0 && (
                <>
                    <span aria-hidden="true" style={{ width: 1, alignSelf: 'stretch', background: c.gridLine, margin: '0 2px', flexShrink: 0 }} />
                    <span style={{
                        fontFamily: tokens.fonts.mono, fontSize: fs(11), letterSpacing: 0.6,
                        textTransform: 'uppercase', color: c.faint, fontWeight: 600, flexShrink: 0,
                    }}>Tags</span>
                    {visibleTags.map((tag, i) => renderTagPill(tag, fitTagsToWidth ? (el) => { tagPillRefs.current[i] = el; } : undefined))}
                    {hiddenTagCount > 0 && (
                        fitTagsToWidth ? (
                            <button
                                ref={hiddenTagsButtonRef}
                                type="button"
                                onClick={toggleHiddenTagsPopover}
                                aria-haspopup="true"
                                aria-expanded={isHiddenTagsOpen}
                                aria-label={`Show ${hiddenTagCount} more tag${hiddenTagCount === 1 ? '' : 's'}`}
                                title={`${hiddenTagCount} more tag${hiddenTagCount === 1 ? '' : 's'}`}
                                style={{
                                    ...pillBase, cursor: 'pointer',
                                    fontFamily: tokens.fonts.mono, fontSize: fs(11), letterSpacing: 0.3, padding: '4px 9px',
                                    background: isHiddenTagsOpen ? c.gridLine : 'transparent',
                                }}
                            >
                                +{hiddenTagCount}
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setShowAllTags(true)}
                                aria-label={`Show ${hiddenTagCount} more tag${hiddenTagCount === 1 ? '' : 's'}`}
                                style={{
                                    ...pillBase, cursor: 'pointer', color: c.faint,
                                    fontFamily: tokens.fonts.mono, letterSpacing: 0.5, padding: '4px 9px',
                                }}
                            >
                                &hellip;
                            </button>
                        )
                    )}
                    {!fitTagsToWidth && showAllTags && tagList.length > TOP_TAGS && (
                        <button
                            type="button"
                            onClick={() => setShowAllTags(false)}
                            aria-label="Show fewer tags"
                            style={{
                                ...pillBase, cursor: 'pointer', color: c.faint,
                                fontFamily: tokens.fonts.mono, fontSize: fs(11), letterSpacing: 0.3, padding: '4px 9px',
                            }}
                        >
                            Show less
                        </button>
                    )}
                </>
            )}
        </>
    );

    if (!fitTagsToWidth) return content;

    return (
        <>
            <div
                ref={rowRef}
                style={{
                    display: 'flex', alignItems: 'center', gap: 16,
                    flexWrap: 'nowrap', overflow: 'hidden', width: '100%',
                }}
            >
                {content}
            </div>
            {/* Portaled so the row's overflow:hidden (needed to clip tag pills
                to one line) doesn't also clip this popover. */}
            {isHiddenTagsOpen && hiddenTagsPopoverPos && createPortal(
                <div
                    ref={hiddenTagsPopoverRef}
                    role="menu"
                    aria-label="More tags"
                    style={{
                        position: 'fixed',
                        top: hiddenTagsPopoverPos.top,
                        bottom: hiddenTagsPopoverPos.bottom,
                        left: hiddenTagsPopoverPos.left,
                        display: 'flex', flexWrap: 'wrap', alignContent: 'flex-start', gap: 6,
                        width: POPOVER_WIDTH, maxHeight: hiddenTagsPopoverPos.maxHeight,
                        overflowY: 'auto', padding: 10, borderRadius: 10,
                        background: c.surface, border: `1px solid ${c.gridLine}`,
                        boxShadow: `0 8px 24px ${tokens.mode === 'dark' ? 'rgba(0,0,0,.5)' : 'rgba(30,36,48,.18)'}`,
                        zIndex: 1000,
                    }}
                >
                    {tagList.slice(fitCount).map((tag) => renderTagPill(tag))}
                </div>,
                document.body
            )}
        </>
    );
};

export default CategoryTagFilters;
